/**
 * Pricing → Configuración (`/pricing/configuracion`).
 *
 * Puerto de `Pricing::configuracion` + `views/pricing/configuracion.php` y de
 * `PricingConfig_model`: la ubicación y la hora de la consulta programada
 * (`pricing_config`), con su alta, edición, activación y borrado.
 *
 * Reglas del panel anterior que se conservan:
 * - Departamento, provincia, distrito y hora son obligatorios, y la hora va en
 *   `HH:MM` o `HH:MM:SS`.
 * - No se admite otra configuración con la misma combinación de ubicación y hora.
 * - Se guarda el usuario que la crea, que se muestra en el listado.
 */

import {
  ArrowLeft,
  CalendarClock,
  CircleCheck,
  Pencil,
  Plus,
  Power,
  Settings,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { idUbigeo } from '@/hooks/use-ubigeo';
import { ApiError } from '@/lib/http/api-error';
import { cargarUbigeo, distritosDe, provinciasDe, type OpcionUbigeo } from '@/lib/ubigeo';
import { cn } from '@/lib/utils';

import {
  useCambiarEstadoConfiguracion,
  useConfiguracionPricing,
  useEliminarConfiguracion,
  useGuardarConfiguracionPricing,
} from '../pricing.api';
import type { ConfiguracionPricing } from '../pricing.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const FORMULARIO_VACIO = {
  id: undefined as number | undefined,
  departamento: '',
  provincia: '',
  distrito: '',
  hora_consulta: '',
};

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function PricingConfiguracionPage() {
  useDocumentTitle('Pricing · Configuración');

  const consulta = useConfiguracionPricing();
  const guardar = useGuardarConfiguracionPricing();
  const cambiarEstado = useCambiarEstadoConfiguracion();
  const eliminar = useEliminarConfiguracion();

  /** Lo que el usuario está escribiendo; `null` mientras no toque nada. */
  const [edicion, setEdicion] = useState<typeof FORMULARIO_VACIO | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<ConfiguracionPricing | null>(null);

  // Catálogo de ubigeo para elegir la ubicación con selects en cascada. Se
  // guarda a qué ubicación pertenece cada lista cargada para no enseñar opciones
  // de otra zona mientras llega la nueva.
  const [departamentos, setDepartamentos] = useState<OpcionUbigeo[]>([]);
  const [provincias, setProvincias] = useState<OpcionUbigeo[]>([]);
  const [provinciasDeId, setProvinciasDeId] = useState('');
  const [distritos, setDistritos] = useState<OpcionUbigeo[]>([]);
  const [distritosDeId, setDistritosDeId] = useState('');
  const [errorUbigeo, setErrorUbigeo] = useState<string | null>(null);

  // Sin edición en curso el formulario muestra la configuración activa, que es
  // como venía la vista del panel anterior al abrirse.
  const activa = consulta.data?.activa;
  const formulario =
    edicion ??
    (activa
      ? {
          id: activa.id,
          departamento: activa.departamento,
          provincia: activa.provincia,
          distrito: activa.distrito,
          hora_consulta: activa.hora_consulta,
        }
      : FORMULARIO_VACIO);

  // Departamentos una sola vez.
  useEffect(() => {
    let vigente = true;

    cargarUbigeo()
      .then((datos) => {
        if (!vigente) return;

        setDepartamentos(datos.departamentos);
        setErrorUbigeo(null);
      })
      .catch((fallo: unknown) => {
        if (!vigente) return;

        setErrorUbigeo(
          fallo instanceof Error ? fallo.message : 'No se pudo cargar el catálogo de ubigeo.',
        );
      });

    return () => {
      vigente = false;
    };
  }, []);

  // Provincias de la ubicación del formulario (edición, activa o nueva).
  useEffect(() => {
    let vigente = true;
    const id = idUbigeo(departamentos, formulario.departamento);

    if (!id) return undefined;

    provinciasDe(id)
      .then((lista) => {
        if (!vigente) return;

        setProvincias(lista);
        setProvinciasDeId(id);
      })
      .catch(() => {
        if (!vigente) return;

        setProvincias([]);
        setProvinciasDeId(id);
      });

    return () => {
      vigente = false;
    };
  }, [departamentos, formulario.departamento]);

  // Distritos de la provincia elegida.
  useEffect(() => {
    let vigente = true;
    const id = idUbigeo(provincias, formulario.provincia);

    if (!id) return undefined;

    distritosDe(id)
      .then((lista) => {
        if (!vigente) return;

        setDistritos(lista);
        setDistritosDeId(id);
      })
      .catch(() => {
        if (!vigente) return;

        setDistritos([]);
        setDistritosDeId(id);
      });

    return () => {
      vigente = false;
    };
  }, [provincias, formulario.provincia]);

  // Listas que corresponden al valor actual del formulario.
  const idDepartamento = idUbigeo(departamentos, formulario.departamento);
  const provinciasVisibles = provinciasDeId === idDepartamento ? provincias : [];
  const idProvincia = idUbigeo(provinciasVisibles, formulario.provincia);
  const distritosVisibles = distritosDeId === idProvincia ? distritos : [];

  function editar(configuracion: ConfiguracionPricing) {
    setError(null);
    setAviso(null);
    setEdicion({
      id: configuracion.id,
      departamento: configuracion.departamento,
      provincia: configuracion.provincia,
      distrito: configuracion.distrito,
      hora_consulta: configuracion.hora_consulta,
    });
  }

  function enviar() {
    setError(null);
    setAviso(null);

    guardar.mutate(
      {
        id: formulario.id,
        departamento: formulario.departamento,
        provincia: formulario.provincia,
        distrito: formulario.distrito,
        hora_consulta: formulario.hora_consulta,
        activo: true,
      },
      {
        onSuccess: () => {
          setAviso('Configuración guardada.');
          setEdicion(null);
        },
        onError: (fallo) =>
          setError(mensajeDe(fallo, 'No se pudo guardar la configuración.')),
      },
    );
  }

  const configuraciones = consulta.data?.configs ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/pricing"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Pricing
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Settings className="size-6 text-brand-500" aria-hidden="true" />
          Configuración
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          La ubicación y la hora con la que se programa la consulta de precios, con su historial de
          cambios.
        </p>
      </div>

      {consulta.data?.activa ? (
        <Badge variant="success">
          Consulta activa: {consulta.data.activa.departamento} / {consulta.data.activa.provincia} /{' '}
          {consulta.data.activa.distrito} · {consulta.data.activa.hora_consulta}
        </Badge>
      ) : (
        <Badge variant="warning">Sin consulta activa</Badge>
      )}
    </header>
  );

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Qué programa esta configuración">
        Cada fila es una combinación de <strong>departamento, provincia, distrito y hora</strong>.
        Sólo la configuración activa se ejecuta como consulta programada del día, así que al guardar
        una nueva conviene desactivar la anterior.
      </Alert>

      {aviso ? (
        <Alert variant="success" title="Listo">
          <span className={ICONO_EN_ALERTA}>
            <CircleCheck className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo guardar">
          {error}
        </Alert>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <CalendarClock className="size-4 text-brand-500" aria-hidden="true" />
            {formulario.id ? `Editando la configuración #${formulario.id}` : 'Nueva configuración'}
          </h2>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cf-departamento" className="text-sm font-medium text-ink-700">
                Departamento
              </label>
              <Select
                id="cf-departamento"
                className={CONTROL}
                value={formulario.departamento}
                onChange={(evento) =>
                  setEdicion({
                    ...formulario,
                    departamento: evento.target.value,
                    provincia: '',
                    distrito: '',
                  })
                }
                disabled={departamentos.length === 0}
                searchable
              >
                <option value="">Selecciona un departamento</option>
                {departamentos.map((opcion) => (
                  <option key={opcion.id} value={opcion.name}>
                    {opcion.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cf-provincia" className="text-sm font-medium text-ink-700">
                Provincia
              </label>
              <Select
                id="cf-provincia"
                className={CONTROL}
                value={formulario.provincia}
                onChange={(evento) =>
                  setEdicion({ ...formulario, provincia: evento.target.value, distrito: '' })
                }
                disabled={!formulario.departamento || provinciasVisibles.length === 0}
                searchable
              >
                <option value="">Selecciona una provincia</option>
                {provinciasVisibles.map((opcion) => (
                  <option key={opcion.id} value={opcion.name}>
                    {opcion.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cf-distrito" className="text-sm font-medium text-ink-700">
                Distrito
              </label>
              <Select
                id="cf-distrito"
                className={CONTROL}
                value={formulario.distrito}
                onChange={(evento) =>
                  setEdicion({ ...formulario, distrito: evento.target.value })
                }
                disabled={!formulario.provincia || distritosVisibles.length === 0}
                searchable
              >
                <option value="">Selecciona un distrito</option>
                {distritosVisibles.map((opcion) => (
                  <option key={opcion.id} value={opcion.name}>
                    {opcion.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="cf-hora" className="text-sm font-medium text-ink-700">
                Hora de consulta
              </label>
              <CampoFecha
                id="cf-hora"
                className={CONTROL}
                value={formulario.hora_consulta}
                onChange={(evento) =>
                  setEdicion({ ...formulario, hora_consulta: evento.target.value })
                }
                tipo="time"
              />
            </div>
          </div>

          {errorUbigeo ? <p className="text-xs text-rose-600">{errorUbigeo}</p> : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={enviar}
              disabled={
                guardar.isPending ||
                !formulario.departamento.trim() ||
                !formulario.provincia.trim() ||
                !formulario.distrito.trim() ||
                !formulario.hora_consulta
              }
            >
              <Plus className="mr-1.5 size-4" aria-hidden="true" />
              {guardar.isPending
                ? 'Guardando…'
                : formulario.id
                  ? 'Guardar cambios'
                  : 'Agregar configuración'}
            </Button>
            {formulario.id || formulario.departamento ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setAviso(null);
                  setError(null);
                  setEdicion(FORMULARIO_VACIO);
                }}
                disabled={guardar.isPending}
              >
                Limpiar
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
            <Settings className="size-4 text-brand-500" aria-hidden="true" />
            Configuraciones registradas
          </h2>

          {consulta.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : configuraciones.length === 0 ? (
            <p className="text-sm text-ink-500">
              No hay ninguna configuración registrada todavía.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Ubicación</th>
                    <th className="py-2">Hora</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2">Usuario</th>
                    <th className="py-2">Última edición</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {configuraciones.map((configuracion) => (
                    <tr key={configuracion.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-2 text-ink-700">
                        {configuracion.departamento} / {configuracion.provincia} /{' '}
                        {configuracion.distrito}
                      </td>
                      <td className="py-2 text-ink-700">{configuracion.hora_consulta}</td>
                      <td className="py-2">
                        <Badge variant={configuracion.activo ? 'success' : 'outline'}>
                          {configuracion.activo ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="py-2 text-ink-500">
                        {configuracion.username || configuracion.email || '—'}
                      </td>
                      <td className="py-2 text-ink-500">{configuracion.updated_at ?? '—'}</td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              cambiarEstado.mutate({
                                id: configuracion.id,
                                activo: !configuracion.activo,
                              })
                            }
                            disabled={cambiarEstado.isPending}
                            title={
                              configuracion.activo ? 'Desactivar la consulta' : 'Activar la consulta'
                            }
                          >
                            <Power
                              className={cn(
                                'size-3.5',
                                configuracion.activo ? 'text-success-600' : 'text-ink-300',
                              )}
                              aria-hidden="true"
                            />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => editar(configuracion)}
                            title="Editar"
                          >
                            <Pencil className="size-3.5" aria-hidden="true" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setAEliminar(configuracion)}
                            title="Eliminar"
                            disabled={eliminar.isPending}
                          >
                            <Trash2 className="size-3.5 text-danger-500" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {aEliminar ? (
        <ConfirmDialog
          open
          onClose={() => setAEliminar(null)}
          onConfirm={() =>
            eliminar.mutate(aEliminar.id, {
              onSuccess: () => {
                setAEliminar(null);
                setAviso('Configuración eliminada.');
              },
            })
          }
          title="¿Eliminar la configuración?"
          description={`Se borrará la consulta de ${aEliminar.departamento} / ${aEliminar.provincia} / ${aEliminar.distrito} a las ${aEliminar.hora_consulta}. No afecta a los ficheros de precios ya recogidos.`}
          confirmLabel="Eliminar"
        />
      ) : null}
    </div>
  );
}
